import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useValidation } from './useValidation'
import { useGridData } from '../context/GridDataContext'
import { getRequiredEntityTypesByOverrideType } from '../constants/OverrideTypes'

export const useRowData = ({ initialRowData, overrideType, selectedFields, gridApiRef }) => {
  const { validateRows } = useValidation()
  const { mappingData, isLoadingMappingData, fetchMappingData } = useGridData()
  const [currentGridData, setCurrentGridData] = useState([])
  const [updateCounter, setUpdateCounter] = useState(0)
  const [skipSort, setSkipSort] = useState(false)

  // Track how many times sortRows is called per validation cycle
  const sortCallCounterRef = useRef(0)
  // Use a ref to track if we're in the middle of a refresh
  const isRefreshingRef = useRef(false)

  // Use ref to track what we've already fetched - this won't trigger re-renders
  const fetchedTypesRef = useRef(new Set())

  // Determine required entity types based on override type
  const requiredEntityTypes = useMemo(() => {
    if (!overrideType) {
      return []
    }

    return getRequiredEntityTypesByOverrideType(overrideType)
  }, [overrideType])

  // Single effect to handle all mapping data fetching logic
  useEffect(() => {
    // Skip if no override type or no required types
    if (!overrideType || !requiredEntityTypes.length) {
      return
    }

    // Check if we already fetched for this type
    if (fetchedTypesRef.current.has(overrideType)) {
      return
    }

    // Check if we already have the required data
    const hasRequiredData =
      mappingData && mappingData.panelists && mappingData.products && mappingData.state_and_counties_map

    if (hasRequiredData) {
      // Mark as fetched even though we didn't need to fetch
      fetchedTypesRef.current.add(overrideType)
      return
    }

    // We need to fetch the data
    fetchMappingData(requiredEntityTypes)
    fetchedTypesRef.current.add(overrideType)
  }, [overrideType, requiredEntityTypes, mappingData, fetchMappingData])

  // Define sortRows function without updateCounter dependency
  // This prevents recreating the function when updateCounter changes
  const sortRows = useCallback(
    (rows) => {
      // Increment counter to track how many times sortRows is called
      sortCallCounterRef.current += 1

      // Helper function to check if a row has actionable validation errors
      const rowHasErrors = (row) => {
        return (
          row.validationErrors && Object.values(row.validationErrors).some((colError) => colError.errorTypes?.size > 0)
        )
      }

      if (skipSort) {
        return rows
      }

      return [...rows].sort((a, b) => {
        const aHasErrors = rowHasErrors(a)
        const bHasErrors = rowHasErrors(b)

        // Priority 1: Rows with errors come first
        if (aHasErrors && !bHasErrors) {
          return -1
        }
        if (!aHasErrors && bHasErrors) {
          return 1
        }

        // Priority 2: Duplicate rows come next (if error status is the same)
        const aIsDuplicate = a.isDuplicateRow
        const bIsDuplicate = b.isDuplicateRow
        if (aIsDuplicate && !bIsDuplicate) {
          return -1
        }
        if (!aIsDuplicate && bIsDuplicate) {
          return 1
        }

        const aIsMarkedForDelete = a.userMarkedForDelete
        const bIsMarkedForDelete = b.userMarkedForDelete
        if (aIsMarkedForDelete && !bIsMarkedForDelete) {
          return -1
        }
        if (!aIsMarkedForDelete && bIsMarkedForDelete) {
          return 1
        }

        // If both error and duplicate status are the same, maintain original order
        return 0
      })
    },
    [skipSort],
  )

  // When updateCounter changes, update the currentGridData from the grid
  useEffect(() => {
    if (!gridApiRef?.current || updateCounter === 0) {
      // Initial load from props
      setCurrentGridData(initialRowData || [])
      return
    }

    // Reset sort call counter when we start a new update cycle
    sortCallCounterRef.current = 0

    // Fetch the current data from the grid
    const rowData = []
    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        rowData.push({ ...node.data })
      }
    })

    // Only set the current grid data if we're not doing a direct refresh
    // Otherwise we'll validate twice
    if (!isRefreshingRef.current) {
      setCurrentGridData(rowData)
    }

    // Clear the refreshing flag
    isRefreshingRef.current = false
  }, [gridApiRef, updateCounter, initialRowData, overrideType])

  // Memoize the validated rows and validation summary
  const { validatedRows, validationSummary } = useMemo(() => {
    // Don't validate if we're still loading mapping data or don't have grid data
    if (isLoadingMappingData || !currentGridData?.length) {
      return {
        validatedRows: currentGridData || [],
        validationSummary: {
          errorCounts: {},
          errorRows: {},
          totalErrorCount: 0,
          hasErrors: false,
        },
      }
    }

    const result = validateRows(currentGridData, {
      overrideType,
      overrideFields: selectedFields,
      mappingData,
    })

    // Sort rows by isDuplicateRow property (duplicates first)
    const sortedRows = sortRows(result?.rows)

    return {
      validatedRows: sortedRows,
      validationSummary: result?.summary || {
        errorCounts: {},
        errorRows: {},
        totalErrorCount: 0,
        hasErrors: false,
      },
    }
  }, [currentGridData, overrideType, selectedFields, mappingData, isLoadingMappingData, validateRows, sortRows])

  // Direct update method that bypasses the double render
  const directDataRefresh = useCallback(
    (suppressSort = false) => {
      if (!gridApiRef?.current) {
        return
      }

      // Set refreshing flag to true to prevent the updateCounter effect from setting data
      isRefreshingRef.current = true

      // Reset sort counter
      sortCallCounterRef.current = 0

      // Set the skip sort flag
      setSkipSort(suppressSort)

      // Get the data directly from the grid
      const rowData = []
      gridApiRef.current.forEachNode((node) => {
        if (node.data) {
          rowData.push({ ...node.data })
        }
      })

      // Update the data state directly
      setCurrentGridData(rowData)

      // Increment updateCounter to trigger the effect, but data is already set
      setUpdateCounter((prev) => prev + 1)
    },
    [gridApiRef],
  )

  return {
    validatedRows: validatedRows || [],
    validationSummary: validationSummary || {},
    refreshValidation: (suppressSort = false) => {
      // Use direct refresh method to avoid double render
      directDataRefresh(suppressSort)
    },
  }
}
